<template>
  <modal
    id="guideline-form-modal"
    ref="modal"
    :hide-footer="true"
    width="w-xxl"
  >
    <span slot="header">
      <h5 class="modal-title">
        {{ form.id ? 'Edit Guideline' : 'Create Guideline' }}
      </h5>
    </span>

    <div slot="body">
      <div class="form-group">
        <label>
          Thumbnail
          <small class="text-muted">(list logo, optional)</small>
          <small class="text-muted d-block">{{ thumbnailFormatHint }}</small>
        </label>
        <div class="fg-thumb-box box">
          <dropzone
            v-show="!currentThumbnail"
            id="fg-thumbnail-dropzone"
            ref="thumbnailDropzone"
            :max-number-of-files="1"
            :max-file-size-in-m-b="thumbnailMaxMb"
            :auto-process-queue="false"
            :use-font-awesome="true"
            :accepted-file-types="thumbnailAccept"
            url="#"
            @vdropzone-error="onThumbnailError"
          />
          <div
            v-if="currentThumbnail"
            class="dz-custom-image-preview"
          >
            <img
              :src="currentThumbnail.original"
              alt="thumbnail"
            >
            <span
              class="remove-icon"
              @click="removeThumbnail"
            />
          </div>
        </div>
      </div>

      <div class="form-group">
        <label>Title <small class="text-muted">(max 150 characters)</small></label>
        <input
          v-model="form.title"
          :class="{ 'is-invalid': titleError }"
          class="form-control"
          type="text"
          maxlength="150"
        >
        <div class="fg-field-meta">
          <small
            v-if="titleError"
            class="text-danger"
          >{{ titleError }}</small>
          <small
            v-else-if="titleAtLimit"
            class="text-warning"
          >Maximum length reached — the title can't be longer than {{ titleMaxLength }} characters.</small>
          <span />
          <small
            :class="titleAtLimit ? 'text-warning' : 'text-muted'"
            class="fg-field-counter"
          >{{ titleLength }}/{{ titleMaxLength }}</small>
        </div>
      </div>

      <div class="form-group">
        <faq-guideline-category-selector v-model="selectedCategory" />
      </div>

      <div class="form-group">
        <label>Description</label>
        <textarea
          v-model="form.description"
          :class="{ 'is-invalid': descriptionError }"
          class="form-control"
          rows="2"
        />
        <small
          v-if="descriptionError"
          class="text-danger"
        >{{ descriptionError }}</small>
      </div>

      <div class="form-group">
        <label>Type</label>
        <div class="m-t-xs">
          <div
            class="btn-group"
            role="group"
          >
            <button
              :class="form.type === 'faq' ? 'primary' : 'white'"
              :disabled="!!form.id"
              type="button"
              class="btn btn-sm"
              @click="form.type = 'faq'"
            >
              FAQ
            </button>
            <button
              :class="form.type === 'guideline' ? 'primary' : 'white'"
              :disabled="!!form.id"
              type="button"
              class="btn btn-sm"
              @click="form.type = 'guideline'"
            >
              Guideline
            </button>
          </div>
        </div>
        <small
          v-if="form.id"
          class="text-muted d-block m-t-xs"
        >Type can't be changed after creation.</small>
      </div>

      <!-- FAQ content -->
      <div
        v-if="form.type === 'faq'"
        class="form-group"
      >
        <label>Questions &amp; Answers</label>
        <div
          v-for="(faq, index) in form.faq_contents"
          :key="index"
          class="box p-3 mb-2 fg-faq-item"
        >
          <button
            type="button"
            class="fg-faq-remove"
            title="Remove this pair"
            @click="removeFaq(index)"
          >
            &times;
          </button>
          <input
            v-model="faq.question"
            :class="{ 'is-invalid': hasHtmlTags(faq.question) }"
            class="form-control"
            type="text"
            placeholder="Question"
          >
          <textarea
            v-model="faq.answer"
            :class="{ 'is-invalid': hasHtmlTags(faq.answer) }"
            class="form-control mt-2"
            rows="2"
            placeholder="Answer"
          />
          <small
            v-if="faqContentError(faq)"
            class="text-danger"
          >{{ faqContentError(faq) }}</small>
        </div>
        <button
          class="btn white btn-sm"
          @click="addFaq"
        >
          + Add Q&amp;A
        </button>
      </div>

      <!-- Guideline content -->
      <div
        v-else
        class="form-group"
      >
        <label>Content</label>
        <div ref="editorWrap">
          <vue-editor
            v-model="form.content"
            :editor-toolbar="editorToolbar"
          />
        </div>

        <label class="mt-4 d-block">
          Files
          <small class="text-muted">({{ fileFormatHint }})</small>
          <small class="text-muted d-block">For videos larger than {{ fileMaxMb }}MB, upload a PDF/TXT containing the video link instead.</small>
        </label>
        <dropzone
          id="fg-files-dropzone"
          ref="filesDropzone"
          :max-number-of-files="maxFileCount"
          :max-file-size-in-m-b="fileMaxMb"
          :auto-process-queue="false"
          :use-font-awesome="true"
          :accepted-file-types="fileAccept"
          url="#"
          @vdropzone-file-added="onFileAdded"
          @vdropzone-removed-file="onFileRemoved"
          @vdropzone-error="onFileError"
        />
        <ul
          v-if="queuedFiles.length"
          class="fg-file-list mt-2"
        >
          <faq-guideline-file-row
            v-for="item in queuedFiles"
            :key="item.key"
            :name="item.name"
            :downloadable="item.downloadable"
            @toggle="item.downloadable = $event"
            @remove="removeQueued(item)"
          />
        </ul>
        <ul
          v-if="existingFiles.length"
          class="fg-file-list mt-2"
        >
          <faq-guideline-file-row
            v-for="(media, i) in existingFiles"
            :key="media.id"
            :name="attachmentLabel(media, i)"
            :href="media.original"
            :downloadable="media.is_downloadable"
            @toggle="onExistingToggle(media, $event)"
            @remove="removeExisting(media.id)"
          />
        </ul>
      </div>

      <div class="form-group">
        <label>Status</label>
        <div class="m-t-xs">
          <div
            class="btn-group"
            role="group"
          >
            <button
              v-for="option in statusOptions"
              :key="option.value"
              :class="form.status === option.value ? 'primary' : 'white'"
              type="button"
              class="btn btn-sm"
              @click="form.status = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label>
          Published date <small class="text-muted">(optional)</small>
          <small class="text-muted d-block">Leave blank to publish immediately. A future date schedules the release — past dates aren't allowed.</small>
        </label>
        <datepicker-input
          v-model="publishedAt"
          :min="minPublishedAt"
          name="published_at"
          placeholder="YYYY-MM-DD"
        />
      </div>

      <div class="text-right mt-3">
        <button
          class="btn white btn-fw mr-2"
          data-dismiss="modal"
        >
          Cancel
        </button>
        <button
          :disabled="saving"
          class="btn primary btn-fw"
          @click="save"
        >
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import FaqGuidelineCategorySelector from './faq-guideline-category-selector.vue';
import FaqGuidelineFileRow from './faq-guideline-file-row.vue';
import DatepickerInput from '@js/components/datepicker/datepicker.vue';

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
];

const TITLE_MAX_LENGTH = 150;

// Keep these in sync with UploadFaqGuidelineMedia — the server is the
// authority, these only stop a doomed request from being sent.
const THUMBNAIL_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const THUMBNAIL_MAX_MB = 5;
const FILE_EXTENSIONS = ['pdf', 'pptx', 'ppt', 'mp4', 'mov', 'txt'];
const FILE_MAX_MB = 10;
const MAX_FILE_COUNT = 20;

// php-fpm post_max_size and nginx client_max_body_size are both 20M
// (docker/php-fpm-www.conf, docker/nginx/nginx.conf.template). 20 files x 10MB
// would blow straight past that and PHP discards an oversized body silently, so
// uploads are split into batches under this budget rather than sent as one POST.
const UPLOAD_BUDGET_BYTES = 18 * 1024 * 1024;

// Quill builds its own toolbar DOM, so the controls can only be labelled
// imperatively. Selector => tooltip text.
const EDITOR_TOOLTIPS = {
    '.ql-bold': 'Bold',
    '.ql-italic': 'Italic',
    '.ql-underline': 'Underline',
    '.ql-strike': 'Strikethrough',
    '.ql-blockquote': 'Blockquote',
    '.ql-code-block': 'Code block',
    '.ql-clean': 'Clear formatting',
    '.ql-list[value="ordered"]': 'Numbered list',
    '.ql-list[value="bullet"]': 'Bulleted list',
    '.ql-list[value="check"]': 'Checklist',
    '.ql-indent[value="-1"]': 'Decrease indent',
    '.ql-indent[value="+1"]': 'Increase indent',
    '.ql-align': 'Text alignment',
    '.ql-header': 'Heading level',
    '.ql-color': 'Text colour',
    '.ql-background': 'Highlight colour',
};

function extensionOf(name) {
    return (name || '')
        .split('.')
        .pop()
        .toLowerCase();
}

function formatMb(bytes) {
    return (bytes / (1024 * 1024)).toFixed(1);
}

function emptyForm() {
    return {
        id: null,
        type: 'faq',
        title: '',
        description: '',
        status: 'draft',
        content: '',
        faq_contents: [{ question: '', answer: '' }],
    };
}

export default {
    components: {
        FaqGuidelineCategorySelector,
        FaqGuidelineFileRow,
        DatepickerInput,
    },

    data() {
        return {
            form: emptyForm(),
            selectedCategory: null,
            publishedAt: null,
            statusOptions: STATUS_OPTIONS,
            existingFiles: [],
            queuedFiles: [],
            // Existing-file changes are buffered and applied on Save (so Cancel
            // reverts): ids to delete + original downloadable flags to diff.
            removedExistingIds: [],
            existingDownloadableOriginal: {},
            currentThumbnail: null,
            removedThumbnailId: null,
            saving: false,
            // published_at as stored when the modal opened. An item already
            // published in the past keeps its date, so the picker's lower bound
            // has to allow that value even though new past dates are refused.
            originalPublishedAt: null,
            // Files in the upload request currently in flight, so a server
            // error keyed files.N can be reported against the right file.
            uploadingBatch: null,
            // Guideline content toolbar — no link/image/video (uploads are
            // disabled; media go through the Files dropzone instead).
            editorToolbar: [
                [{ header: [false, 1, 2, 3, 4, 5, 6] }],
                ['bold', 'italic', 'underline', 'strike'],
                [
                    { align: '' },
                    { align: 'center' },
                    { align: 'right' },
                    { align: 'justify' },
                ],
                ['blockquote', 'code-block'],
                [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ color: [] }, { background: [] }],
                ['clean'],
            ],
        };
    },

    computed: {
        titleMaxLength() {
            return TITLE_MAX_LENGTH;
        },

        titleLength() {
            return (this.form.title || '').length;
        },

        titleAtLimit() {
            return this.titleLength >= TITLE_MAX_LENGTH;
        },

        titleError() {
            return this.htmlTagsError(this.form.title);
        },

        descriptionError() {
            return this.htmlTagsError(this.form.description);
        },

        thumbnailMaxMb() {
            return THUMBNAIL_MAX_MB;
        },

        thumbnailAccept() {
            return THUMBNAIL_EXTENSIONS.map(ext => `.${ext}`).join(',');
        },

        thumbnailFormatHint() {
            return `${THUMBNAIL_EXTENSIONS.join(
                ', ',
            ).toUpperCase()} — max ${THUMBNAIL_MAX_MB}MB`;
        },

        fileMaxMb() {
            return FILE_MAX_MB;
        },

        maxFileCount() {
            return MAX_FILE_COUNT;
        },

        fileAccept() {
            return FILE_EXTENSIONS.map(ext => `.${ext}`).join(',');
        },

        fileFormatHint() {
            return `${FILE_EXTENSIONS.join(
                ', ',
            ).toUpperCase()} — max ${FILE_MAX_MB}MB each, up to ${MAX_FILE_COUNT} files`;
        },

        // Backdating isn't valid (PS-8898), but don't invalidate the stored date
        // of something already published in the past.
        minPublishedAt() {
            const today = moment().startOf('day');

            if (
                this.originalPublishedAt &&
                this.originalPublishedAt.isBefore(today)
            ) {
                return this.originalPublishedAt.clone().startOf('day');
            }

            return today;
        },
    },

    watch: {
        // The editor only exists in the guideline branch, so it mounts fresh
        // when the type is switched — re-label the toolbar then.
        'form.type'(type) {
            if (type === 'guideline') {
                this.initEditorTooltips();
            }
        },
    },

    methods: {
        // hasHtmlTags / htmlTagsError come from the global mixin (mixins/index.js).
        faqContentError(faq) {
            return (
                this.htmlTagsError(faq.question) ||
                this.htmlTagsError(faq.answer)
            );
        },

        show(guideline = null) {
            this.existingFiles = [];
            this.queuedFiles = [];
            this.removedExistingIds = [];
            this.existingDownloadableOriginal = {};
            this.currentThumbnail = null;
            this.removedThumbnailId = null;
            this.uploadingBatch = null;
            this.resetDropzones();

            if (guideline) {
                this.form = {
                    id: guideline.id,
                    type: guideline.type,
                    title: guideline.title,
                    description: guideline.description,
                    status: guideline.status,
                    content: guideline.guideline_content
                        ? guideline.guideline_content.content
                        : '',
                    faq_contents:
                        guideline.faq_contents && guideline.faq_contents.length
                            ? guideline.faq_contents.map(f => ({
                                  question: f.question,
                                  answer: f.answer,
                              }))
                            : [{ question: '', answer: '' }],
                };
                this.selectedCategory = guideline.category || null;
                this.publishedAt = guideline.published_at
                    ? moment(guideline.published_at)
                    : null;
                this.originalPublishedAt = this.publishedAt
                    ? this.publishedAt.clone()
                    : null;
                this.existingFiles = (guideline.media_urls || []).filter(
                    m => m.collection_name === 'faq_guideline_files',
                );
                this.existingDownloadableOriginal = this.existingFiles.reduce(
                    (map, m) => ({ ...map, [m.id]: !!m.is_downloadable }),
                    {},
                );
                this.currentThumbnail =
                    (guideline.media_urls || []).find(
                        m => m.collection_name === 'faq_guideline_thumbnail',
                    ) || null;
            } else {
                this.form = emptyForm();
                this.selectedCategory = null;
                this.publishedAt = null;
                this.originalPublishedAt = null;
            }

            this.$refs.modal.$emit('show');
            this.initEditorTooltips();
        },

        // Label the Quill toolbar controls. Scoped to this form's editor via
        // $refs.editorWrap so the shared global `vue-editor` stays untouched.
        initEditorTooltips() {
            this.$nextTick(() => {
                const wrap = this.$refs.editorWrap;

                if (!wrap) {
                    return;
                }

                Object.keys(EDITOR_TOOLTIPS).forEach(selector => {
                    wrap.querySelectorAll(selector).forEach(el => {
                        el.setAttribute('title', EDITOR_TOOLTIPS[selector]);
                    });
                });
            });
        },

        // Clear any files left in the dropzones from a previous open.
        resetDropzones() {
            this.$nextTick(() => {
                if (this.$refs.thumbnailDropzone) {
                    this.$refs.thumbnailDropzone.removeAllFiles(true);
                }
                if (this.$refs.filesDropzone) {
                    this.$refs.filesDropzone.removeAllFiles(true);
                }
            });
        },

        hide() {
            this.$refs.modal.$emit('hide');
        },

        // Newly-dropped files are listed below the dropzone (its built-in
        // previews are hidden via CSS) so they match the detail file list.
        onFileAdded(file) {
            const key = `${file.name}-${file.size}-${file.lastModified}`;

            // Dropzone doesn't dedupe; identical keys would collide in v-for and
            // silently double-upload. Reject the duplicate.
            if (this.queuedFiles.some(f => f.key === key)) {
                this.$refs.filesDropzone.removeFile(file);
                this.swalWarning({ text: 'This file is already added.' });
                return;
            }

            // Belt-and-braces on top of the dropzone's accept list: reject an
            // unsupported extension here too, so it never reaches queuedFiles
            // and can't look uploaded.
            if (!FILE_EXTENSIONS.includes(extensionOf(file.name))) {
                this.rejectFile(
                    this.$refs.filesDropzone,
                    file,
                    `"${file.name}" is not a supported file type. Allowed: ${
                        this.fileFormatHint
                    }.`,
                );
                return;
            }

            // Reactive (not frozen) so the "Allow download" checkbox works.
            // Vue only observes own-enumerable props; a native File has none,
            // so `file` is not deep-observed.
            this.queuedFiles.push({
                key,
                name: file.name,
                file,
                downloadable: false,
            });
        },

        onFileRemoved(file) {
            const index = this.queuedFiles.findIndex(f => f.file === file);
            if (index !== -1) {
                this.queuedFiles.splice(index, 1);
            }
        },

        removeQueued(item) {
            // Removing from the dropzone emits removedfile → onFileRemoved.
            this.$refs.filesDropzone.removeFile(item.file);
        },

        // A rejected file must be removed, not left sitting in the dropzone
        // looking uploaded — getAcceptedFiles() skips it, so nothing would be
        // sent while the preview still suggests otherwise.
        rejectFile(dropzone, file, message, fallback) {
            if (dropzone) {
                dropzone.removeFile(file);
            }

            this.swalWarning({
                text:
                    typeof message === 'string' && message ? message : fallback,
            });
        },

        onFileError(file, message) {
            this.rejectFile(
                this.$refs.filesDropzone,
                file,
                message,
                `This file could not be added. Allowed: ${
                    this.fileFormatHint
                }.`,
            );
        },

        onThumbnailError(file, message) {
            this.rejectFile(
                this.$refs.thumbnailDropzone,
                file,
                message,
                `This image could not be added. Allowed: ${
                    this.thumbnailFormatHint
                }.`,
            );
        },

        addFaq() {
            this.form.faq_contents.push({ question: '', answer: '' });
        },

        removeFaq(index) {
            this.form.faq_contents.splice(index, 1);
        },

        ext(media) {
            return (media.original || '')
                .split('.')
                .pop()
                .toLowerCase();
        },

        attachmentLabel(media, index) {
            return `Attachments #${index + 1}.${this.ext(media)}`;
        },

        removeThumbnail() {
            if (this.currentThumbnail) {
                this.removedThumbnailId = this.currentThumbnail.id;
            }
            this.currentThumbnail = null;
        },

        // Local only — persisted on Save (so Cancel reverts).
        onExistingToggle(media, value) {
            media.is_downloadable = value;
        },

        // Buffer the removal; the DELETE happens on Save.
        removeExisting(mediaId) {
            this.removedExistingIds.push(mediaId);
            this.existingFiles = this.existingFiles.filter(
                m => m.id !== mediaId,
            );
        },

        buildPayload() {
            const payload = {
                title: this.form.title,
                description: this.form.description,
                category_id: this.selectedCategory
                    ? this.selectedCategory.id
                    : null,
                status: this.form.status,
                published_at: this.publishedAt
                    ? this.publishedAt.format('YYYY-MM-DD[T]HH:mm:ssZZ')
                    : null,
            };

            if (!this.form.id) {
                payload.type = this.form.type;
            }

            if (this.form.type === 'faq') {
                payload.faq_contents = this.form.faq_contents
                    .filter(f => f.question || f.answer)
                    .map((f, i) => ({ ...f, sort_order: i }));
            } else {
                payload.content = this.form.content;
            }

            return payload;
        },

        /**
         * Everything that can be checked without hitting the server, checked
         * BEFORE the record is written. Previously the record was created first
         * and the media posted after, so a rejected attachment left a saved
         * guideline with nothing attached.
         *
         * @return {string} the first problem found, or '' when valid
         */
        validateBeforeSave(thumbnail) {
            if (!this.form.title) {
                return 'Title is required.';
            }
            if (this.titleError) {
                return `Title: ${this.titleError}`;
            }
            if (!this.selectedCategory) {
                return 'Category is required.';
            }
            if (this.descriptionError) {
                return `Description: ${this.descriptionError}`;
            }

            if (this.form.type === 'faq') {
                const tagged = this.form.faq_contents
                    .map(faq => this.faqContentError(faq))
                    .find(Boolean);

                if (tagged) {
                    return `Questions & Answers: ${tagged}`;
                }
            }

            if (thumbnail) {
                if (
                    !THUMBNAIL_EXTENSIONS.includes(extensionOf(thumbnail.name))
                ) {
                    return `Thumbnail "${
                        thumbnail.name
                    }" is not a supported image. Allowed: ${
                        this.thumbnailFormatHint
                    }.`;
                }
                if (thumbnail.size > THUMBNAIL_MAX_MB * 1024 * 1024) {
                    return `Thumbnail "${thumbnail.name}" is ${formatMb(
                        thumbnail.size,
                    )}MB — the limit is ${THUMBNAIL_MAX_MB}MB.`;
                }
            }

            for (const item of this.queuedFiles) {
                if (!FILE_EXTENSIONS.includes(extensionOf(item.name))) {
                    return `"${
                        item.name
                    }" is not a supported file type. Allowed: ${
                        this.fileFormatHint
                    }.`;
                }
                if (item.file.size > FILE_MAX_MB * 1024 * 1024) {
                    return `"${item.name}" is ${formatMb(
                        item.file.size,
                    )}MB — the limit is ${FILE_MAX_MB}MB per file.`;
                }
            }

            return '';
        },

        /**
         * Split the queued files into batches that each stay under the server's
         * request-body cap, so a large selection can't silently exceed
         * post_max_size, and one rejected batch can't take the others with it.
         */
        buildUploadBatches() {
            const batches = [];
            let current = [];
            let currentSize = 0;

            this.queuedFiles.forEach(item => {
                if (
                    current.length &&
                    currentSize + item.file.size > UPLOAD_BUDGET_BYTES
                ) {
                    batches.push(current);
                    current = [];
                    currentSize = 0;
                }

                current.push(item);
                currentSize += item.file.size;
            });

            if (current.length) {
                batches.push(current);
            }

            return batches;
        },

        // Sends one media change set (uploads, removals, toggles).
        sendMediaRequest(id, formData) {
            return this.$store.dispatch(
                'faqGuideline/uploadFaqGuidelineMedia',
                {
                    id,
                    formData,
                },
            );
        },

        /**
         * Every media change for this Save, in as few requests as possible.
         *
         * The whole change set travels together — thumbnail, new files,
         * removals and download-flag toggles — because the server publishes one
         * audit entry per request, snapshotting before it mutates. Splitting
         * these would produce several entries per Save and make the before/after
         * unreadable.
         *
         * Only an oversized selection splits, and only across `files[]`:
         * post_max_size caps a request at 20 MB. Removals, toggles and the
         * thumbnail all ride in the first batch.
         */
        async persistMedia(id, thumbnail) {
            const removals = this.removedExistingIds.slice();

            // A thumbnail dropped without a replacement is just another removal.
            if (!thumbnail && this.removedThumbnailId) {
                removals.push(this.removedThumbnailId);
            }

            const toggles = this.downloadableChanges();
            const batches = this.buildUploadBatches();

            if (
                !thumbnail &&
                !removals.length &&
                !toggles.length &&
                !batches.length
            ) {
                return;
            }

            // At least one request, even when there are no files to upload.
            const rounds = batches.length ? batches : [[]];

            for (const [index, batch] of rounds.entries()) {
                const formData = new FormData();

                // Everything that isn't a file upload goes in the first request.
                if (index === 0) {
                    if (thumbnail) {
                        formData.append('thumbnail', thumbnail);
                    }

                    removals.forEach(mediaId => {
                        formData.append('remove_media_ids[]', mediaId);
                    });

                    toggles.forEach(media => {
                        formData.append(
                            `downloadable_updates[${media.id}]`,
                            media.is_downloadable ? 1 : 0,
                        );
                    });
                }

                // downloadable[] must stay index-aligned with files[] within
                // the same request.
                batch.forEach(item => {
                    formData.append('files[]', item.file);
                    formData.append(
                        'downloadable[]',
                        item.downloadable ? 1 : 0,
                    );
                });

                // Server error keys (files.0, files.1, …) are indexed within
                // the request, so remember which batch is in flight to resolve
                // them back to a file name. Left set on throw — firstError()
                // reads it; cleared only once the batch succeeds.
                this.uploadingBatch = batch;
                await this.sendMediaRequest(id, formData);
                this.uploadingBatch = null;
            }
        },

        // Existing files whose "Allow download" checkbox was toggled this open.
        downloadableChanges() {
            return this.existingFiles.filter(
                media =>
                    !!media.is_downloadable !==
                    !!this.existingDownloadableOriginal[media.id],
            );
        },

        async save() {
            const thumbnail = this.$refs.thumbnailDropzone
                ? this.$refs.thumbnailDropzone.getAcceptedFiles()[0]
                : null;

            const problem = this.validateBeforeSave(thumbnail);
            if (problem) {
                this.swalWarning({ text: problem });
                return;
            }

            this.saving = true;
            try {
                const payload = this.buildPayload();
                let id = this.form.id;

                // The record goes first, and media second. Doing it the other
                // way round makes the record's audit entry read the *new* media
                // as its "before" state, since the snapshot is taken when the
                // update request starts.
                if (id) {
                    await this.$store.dispatch(
                        'faqGuideline/updateFaqGuideline',
                        { id, payload },
                    );
                } else {
                    const created = await this.$store.dispatch(
                        'faqGuideline/createFaqGuideline',
                        payload,
                    );
                    id = created.id;
                    // Switch to the edit path immediately so a failure in a
                    // following step (media upload, etc.) retries as an update
                    // instead of creating a second guideline.
                    this.form.id = id;
                }

                await this.persistMedia(id, thumbnail);

                this.hide();
                this.$emit('saved');
                this.swal({ text: 'FAQ Guideline saved.' });
            } catch (error) {
                this.swalError({ text: this.firstError(error) });
            } finally {
                this.saving = false;
            }
        },

        firstError(error) {
            const errors = _.get(error, 'response.data.errors');
            if (errors) {
                const firstKey = Object.keys(errors)[0];
                return this.describeErrorKey(firstKey, errors[firstKey][0]);
            }

            // An oversized body is dropped before PHP populates the request, so
            // the usual validation envelope never arrives.
            if (_.get(error, 'response.status') === 413) {
                return 'The upload is too large. Please attach fewer or smaller files.';
            }

            return _.get(
                error,
                'response.data.message',
                'Unable to save FAQ Guideline.',
            );
        },

        // "files.2 must not be greater than…" is meaningless to the admin —
        // name the file instead.
        describeErrorKey(key, message) {
            const match = /^files\.(\d+)$/.exec(key);

            if (!match) {
                return message;
            }

            const batch = this.uploadingBatch || this.queuedFiles;
            const item = batch[Number(match[1])];

            return item ? `"${item.name}": ${message}` : message;
        },
    },
};
</script>

<style>
#guideline-form-modal .modal-dialog {
    max-width: 820px;
}
/* Hide dropzone.js's built-in file previews; queued files are listed below
   instead. Unscoped because the dropzone DOM lacks the scoped attribute. */
#fg-files-dropzone .dz-preview {
    display: none !important;
}
/* Keep the drop/click prompt visible after files are added. */
#fg-files-dropzone.dz-started .dz-message {
    display: block !important;
}
</style>

<style scoped>
.fg-thumb-box {
    max-width: 240px;
    padding: 0;
}
/* Message on the left, character counter pinned right. */
.fg-field-meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
}
.fg-field-counter {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
}
.fg-file-list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.fg-faq-item {
    position: relative;
    padding-right: 40px;
}
.fg-faq-remove {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #adb5bd;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    border-radius: 4px;
}
.fg-faq-remove:hover {
    color: #e8554e;
    background: #fbeae6;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/faq_guideline/faq-guideline-form.vue